import React, { useMemo } from "react";
import { Box, Typography, CircularProgress, Tooltip } from "@mui/material";
import { Handle, Position, useStore } from "@xyflow/react";
import { NodeControls } from "./nodeControls";
import { useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import azureLogo from "../../../../assets/aureLogo.png";

export const OpenAINode = ({ id, data, selected, highlighted }) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";

  // Get nodes and edges from store for validation
  const nodes = useStore((store) => store.nodes);
  const edges = useStore((store) => store.edges);
  const node = useStore((store) => store.nodes.find((n) => n.id === id));

  // Settings validation
  const settingsValidation = useMemo(() => {
    const settings = data.output?.settings || {};

    const hasModel = settings.modelName && settings.modelName.trim().length > 0;

    return {
      hasModel,
      isConfigured: hasModel,
    };
  }, [data.output?.settings]);

  // Execution validation
  const canExecute = useMemo(() => {
    return settingsValidation.isConfigured;
  }, [settingsValidation]);

  // Status-based styling (following defaultNode pattern)
  const getStatusStyling = () => {
    const baseColor = selected ? "#27BBD9" : highlighted ? "#70C930" : "#666";
    const currentStatus = data.status || node?.status;

    switch (currentStatus) {
      case "running":
        return {
          borderColor: theme.palette.warning.main,
          glowColor: `${theme.palette.warning.main}30`,
          statusColor: theme.palette.warning.main,
          statusLabel: "Running",
        };
      case "pending":
        return {
          borderColor: theme.palette.info.main,
          glowColor: `${theme.palette.info.main}30`,
          statusColor: theme.palette.info.main,
          statusLabel: "Pending",
        };
      case "completed":
        return {
          borderColor: theme.palette.success.main,
          glowColor: `${theme.palette.success.main}30`,
          statusColor: theme.palette.success.main,
          statusLabel: "Completed",
        };
      case "error":
        return {
          borderColor: theme.palette.error.main,
          glowColor: `${theme.palette.error.main}30`,
          statusColor: theme.palette.error.main,
          statusLabel: "Error",
        };
      case "cancelled":
        return {
          borderColor: theme.palette.grey[500],
          glowColor: `${theme.palette.grey[500]}30`,
          statusColor: theme.palette.grey[500],
          statusLabel: "Cancelled",
        };
      default:
        return {
          borderColor: baseColor,
          glowColor: "transparent",
          statusColor: null,
          statusLabel: null,
        };
    }
  };

  const statusStyling = getStatusStyling();

  // Status badge renderer (following defaultNode pattern)
  const renderStatusBadge = () => {
    const currentStatus = data.status || node?.status;
    if (!currentStatus || currentStatus === "idle") return null;

    const getStatusIcon = () => {
      switch (currentStatus) {
        case "running":
          return <CircularProgress size={12} sx={{ color: statusStyling.statusColor }} />;
        case "pending":
          return <PlayArrowIcon sx={{ fontSize: 14, color: statusStyling.statusColor }} />;
        case "completed":
          return <CheckCircleIcon sx={{ fontSize: 14, color: statusStyling.statusColor }} />;
        case "error":
          return <ErrorIcon sx={{ fontSize: 14, color: statusStyling.statusColor }} />;
        case "cancelled":
          return <CancelIcon sx={{ fontSize: 14, color: statusStyling.statusColor }} />;
        default:
          return null;
      }
    };

    return (
      <Box
        sx={{
          position: "absolute",
          top: -6,
          right: -6,
          backgroundColor: "#414243",
          borderRadius: "50%",
          border: `2px solid ${statusStyling.statusColor}`,
          width: 20,
          height: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10,
          boxShadow: `0 2px 8px ${statusStyling.glowColor}`,
          transition: "all 0.2s ease-in-out",
        }}
        role="status"
        aria-label={statusStyling.statusLabel}
        title={statusStyling.statusLabel}
      >
        {getStatusIcon()}
      </Box>
    );
  };

  const borderColor = selected ? "#27BBD9" : highlighted ? "#70C930" : "#666";

  return (
    <Box
      sx={{
        backgroundColor: "#222",
        color: "white",
        borderRadius: 55,
        padding: 2,
        width: 90,
        position: "relative",
        border: `2px solid ${statusStyling.borderColor}`,
        boxShadow:
          statusStyling.glowColor !== "transparent"
            ? `0 0 12px ${statusStyling.glowColor}`
            : "none",
        transition: "border 0.2s, box-shadow 0.2s",
        "&:hover .node-controls": { opacity: 1, pointerEvents: "auto" },
        userSelect: "none",
      }}
    >
      {/* Status Badge */}
      {renderStatusBadge()}

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Top}
        id="output"
        style={{
          backgroundColor: "#00C49F",
          width: 14,
          height: 14,
          borderRadius: "50%",
          left: "50%",
          top: -7,
          transform: "translateX(-50%)",
          border: "2px solid white",
          cursor: "pointer",
          zIndex: 10,
        }}
      />

      {/* Node Controls - only settings and delete */}
      {node?.status !== "running" && (
        <NodeControls
          onBottom={true}
          nodeId={id}
          onSettingsClick={() => data.onSettingsClick?.(id)}
          onDelete={() => data.onDelete?.(id)}
          // No onRun or onDisable - only settings and delete
        />
      )}

      {/* Node Image */}
      <Box
        component="img"
        src={azureLogo}
        alt="Azure Logo"
        sx={{
          width: "100%",
          height: "auto",
          display: "block",
          margin: "0 auto",
        }}
      />

      {/* Warning icon for missing configuration */}
      {!settingsValidation.isConfigured && (
        <Tooltip title="OpenAI model not fully configured">
          <ErrorOutlineIcon
            sx={{
              color: "#ff6b6b",
              position: "absolute",
              top: 8,
              right: 8,
              fontSize: 16,
              cursor: "default",
            }}
          />
        </Tooltip>
      )}

      {/* Optional log/status */}
      {data.log && (
        <Typography
          variant="caption"
          sx={{
            marginTop: 1,
            fontSize: "0.75rem",
            color: "#ccc",
            userSelect: "text",
            whiteSpace: "pre-wrap",
            maxHeight: 80,
            overflowY: "auto",
          }}
        >
          {data.log}
        </Typography>
      )}
    </Box>
  );
};
